import { message, Select, Tag } from "antd";
import { useFetchDebtorProfileQuery } from "features/accountsTable/agencyPortal/accountsTableAPI";
import { useUpdateDebtorTagsMutation } from "features/debtorSider/agencyPortal/debtorSiderAPI";
import { useDebtorTagPermissions } from "features/tags/hooks/useDebtorTagPermissions";
import { useFetchDebtorTagsQuery } from "features/tags/tagsAPI";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";

const StyledCard = styled.div`
  margin-left: 8px;
  margin-bottom: 2px;
`;

const StyledSelect = styled(Select)`
  width: 100%;
`;

const StyledTag = styled(Tag)`
  margin: 2px 2px;
`;

const StyledSectionTitle = styled.h4`
  margin: 2px 0;
`;

export function DebtorTagsCard() {
  const { debtorId } = useParams();
  const [updateDebtorTags] = useUpdateDebtorTagsMutation();
  const { data: debtorPersonalInfo } = useFetchDebtorProfileQuery({ debtorId });
  const { data: debtorTags } = useFetchDebtorTagsQuery();
  const { canAddTag, canRemoveTag } = useDebtorTagPermissions();
  const [currentDebtorTags, setCurrentDebtorTags] = useState({
    id: null,
    tags: [],
  });

  const assignedTags = currentDebtorTags?.tags ?? [];

  useEffect(() => {
    if (debtorPersonalInfo?.id !== undefined && debtorPersonalInfo?.debtorTagIds !== undefined) {
      setCurrentDebtorTags({
        id: debtorPersonalInfo?.id,
        tags: debtorTags?.filter((tag) => debtorPersonalInfo.debtorTagIds.includes(tag.id)) ?? [],
      });
    }
  }, [debtorPersonalInfo?.debtorTagIds, debtorPersonalInfo?.id, debtorTags]);

  const isTagAssigned = (tag) => assignedTags.some((t) => t?.id === tag.id);

  const tagRender = (props) => {
    const { label, value, closable, onClose } = props;
    const tag = assignedTags.find((t) => t?.id === value);
    const onPreventMouseDown = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <StyledTag
        key={value}
        color={tag?.isSystem ? "red" : "blue"}
        onMouseDown={onPreventMouseDown}
        closable={closable && canRemoveTag(tag)}
        onClose={onClose}
      >
        {label}
      </StyledTag>
    );
  };

  const onTagChange = async (_, options) => {
    const selectedTags = options.map((option) => option.tagOption);

    // Check permissions for added tags. Ideally, this should not even be possible due to disabled options,
    // but we just added some extra safety checks here in case of any bugs or edge cases.
    const addedTags = selectedTags.filter((tag) => !isTagAssigned(tag));
    if (addedTags.some((tag) => !canAddTag(tag))) {
      message.warning("You don't have permission to add some of these tags.");
      return;
    }

    // Check permissions for removed tags. Again, this should not be possible due to disabled options, but
    // we want to be safe.
    const removedTags = assignedTags.filter(
      (prevTag) => !selectedTags.some((tag) => tag.id === prevTag.id),
    );
    if (removedTags.some((tag) => !canRemoveTag(tag))) {
      message.warning("You don't have permission to remove some of these tags.");
      return;
    }

    const selectedTagIds = selectedTags.map((tag) => tag.id);
    const savedTagIds = [...(debtorPersonalInfo?.debtorTagIds ?? [])];

    // Skip if no change
    if (JSON.stringify(selectedTagIds.sort()) === JSON.stringify(savedTagIds.sort())) {
      return;
    }

    if (!debtorPersonalInfo?.id) {
      return;
    }

    setCurrentDebtorTags({ id: debtorPersonalInfo.id, tags: selectedTags });

    const result = await updateDebtorTags({
      debtorId: debtorPersonalInfo.id,
      debtorTagIds: selectedTagIds,
    });
    if ("data" in result) {
      message.success("Tags updated successfully!");
    } else {
      message.error("Unable to update tags.");
    }
  };

  const tagOptions = debtorTags?.map((tag) => {
    const assigned = isTagAssigned(tag);
    const disabled = assigned ? !canRemoveTag(tag) : !canAddTag(tag);
    return {
      value: tag.id,
      label: tag.name,
      tagOption: tag,
      disabled,
    };
  });

  return (
    <StyledCard>
      <StyledSectionTitle>Debtor Tags</StyledSectionTitle>
      <StyledSelect
        maxTagTextLength={20}
        popupMatchSelectWidth={false}
        mode="multiple"
        placeholder="Select Debtor Tags..."
        filterOption={(input, option) => option.label?.toLowerCase().includes(input.toLowerCase())}
        tagRender={tagRender}
        onChange={onTagChange}
        options={tagOptions}
        value={assignedTags.map((tag) => tag?.id) ?? []}
      />
    </StyledCard>
  );
}
